<template>
  <!--
    ProjectManager — the Project_Manager container (task 14.1).

    Mounted inside the Admin_Shell (via the `/admin/projects` route registered
    in task 14.2), this container hosts the Project_List and owns the
    cross-cutting concerns that sit above it (Requirement 1.2):

      - success / error snackbars for the outcomes of project actions
        (create, update, reorder, visibility toggle, delete)
        (Requirements 4.5, 5.4, 8.8, 9.4, 10.4);
      - the fetched managed-project collection, held here after the
        Project_List loads it so container-level actions can reason about it.

    The Project_Editor is a sibling route (`/admin/projects/new` and
    `/admin/projects/:id`), so `add` / `edit` events from the list navigate to
    it. When the editor saves and returns to this route it appends a `saved`
    query flag; the container surfaces the matching success confirmation on
    arrival (Requirements 4.5, 5.4).

    Uses the Options API to match the project's `plugin:vue/essential` ESLint
    preset and the existing admin components' conventions.
  -->
  <div class="project-manager">
    <div class="project-manager__header">
      <h2 class="project-manager__heading">Manage projects</h2>
      <v-btn
        color="#e7511e"
        prepend-icon="mdi-plus"
        class="text-none"
        data-test="add-project"
        @click="goToCreate"
      >
        Add project
      </v-btn>
    </div>

    <!--
      Project_List. The container listens to every outcome the list can
      surface: `loaded` to hold the fetched collection, `add` / `edit` to route
      to the editor, `reordered` / `visibility-changed` / `deleted` to raise the
      success snackbar (each carries a human-readable `.message`), and `error`
      to raise the error snackbar (also `{ message }`). The list owns its own
      delete confirmation dialog (task 12.2), so the container only reacts to
      the resulting outcome events; wiring them here keeps the feedback surfaces
      in one place (Requirements 8.8, 9.4, 10.4).
    -->
    <ProjectList
      ref="list"
      @loaded="onProjectsLoaded"
      @add="goToCreate"
      @edit="goToEdit"
      @reordered="onActionSuccess"
      @visibility-changed="onActionSuccess"
      @deleted="onActionSuccess"
      @error="onActionError"
    />

    <!-- Success confirmation snackbar (Requirements 4.5, 5.4, 8.8, 9.4, 10.4). -->
    <v-snackbar
      v-model="successSnackbar.open"
      color="success"
      :timeout="SNACKBAR_TIMEOUT"
      location="top"
      data-test="success-snackbar"
    >
      {{ successSnackbar.message }}
      <template #actions>
        <v-btn variant="text" @click="successSnackbar.open = false">Close</v-btn>
      </template>
    </v-snackbar>

    <!-- Error snackbar for failed actions. -->
    <v-snackbar
      v-model="errorSnackbar.open"
      color="error"
      :timeout="SNACKBAR_TIMEOUT"
      location="top"
      data-test="error-snackbar"
    >
      {{ errorSnackbar.message }}
      <template #actions>
        <v-btn variant="text" @click="errorSnackbar.open = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
// ProjectManager — Project_Manager container (task 14.1).
import ProjectList from 'components/admin/ProjectList.vue'

// How long the feedback snackbars stay visible (ms).
const SNACKBAR_TIMEOUT = 4000

// Success messages keyed by the `saved` query flag the editor appends when it
// returns to this route after a successful create / update (Requirements 4.5,
// 5.4).
const SAVED_MESSAGES = {
  created: 'Project created.',
  updated: 'Project updated.'
}

export default {
  name: 'ProjectManager',

  components: {
    ProjectList
  },

  data () {
    return {
      SNACKBAR_TIMEOUT,

      // The managed-project collection, held here once the Project_List loads
      // it so container-level concerns can reason about the current set.
      projects: [],

      successSnackbar: {
        open: false,
        message: ''
      },

      errorSnackbar: {
        open: false,
        message: ''
      }
    }
  },

  // Surface a success confirmation when arriving from the editor after a
  // successful save (Requirements 4.5, 5.4). Handled on both initial mount and
  // subsequent navigations to this route.
  mounted () {
    this.handleSavedQuery()
  },

  watch: {
    '$route.query.saved' () {
      this.handleSavedQuery()
    }
  },

  methods: {
    // Hold the fetched managed-project collection emitted by the Project_List.
    onProjectsLoaded (projects) {
      this.projects = Array.isArray(projects) ? projects : []
    },

    // Navigate to the editor in create mode. Route registration is task 14.2;
    // the container drives navigation to the named route.
    goToCreate () {
      this.$router.push({ name: 'admin-project-new' })
    },

    // Navigate to the editor in edit mode for the given project id.
    goToEdit (id) {
      if (id == null) return
      this.$router.push({ name: 'admin-project-edit', params: { id: String(id) } })
    },

    // Raise the success snackbar (Requirements 4.5, 5.4, 8.8, 9.4, 10.4).
    showSuccess (message) {
      this.successSnackbar.message =
        typeof message === 'string' && message ? message : 'Done.'
      this.successSnackbar.open = true
    },

    // Raise the error snackbar for a failed action.
    showError (message) {
      this.errorSnackbar.message =
        typeof message === 'string' && message
          ? message
          : 'Something went wrong. Please try again.'
      this.errorSnackbar.open = true
    },

    // The Project_List surfaces successful reorder / visibility / delete
    // outcomes as `reordered` / `visibility-changed` / `deleted` events, each
    // carrying a human-readable `.message`. Map that message to the success
    // snackbar (Requirements 8.8, 9.4, 10.4).
    onActionSuccess (payload) {
      this.showSuccess(payload && payload.message)
    },

    // The Project_List surfaces failed actions as an `error` event carrying a
    // `{ message }` payload; map it to the error snackbar.
    onActionError (payload) {
      this.showError(payload && payload.message)
    },

    // Show the success confirmation matching a `saved` query flag left by the
    // editor, then strip the flag so a refresh does not repeat the message.
    handleSavedQuery () {
      const saved = this.$route && this.$route.query && this.$route.query.saved
      const message = SAVED_MESSAGES[saved]
      if (!message) return

      this.showSuccess(message)

      const query = { ...this.$route.query }
      delete query.saved
      this.$router.replace({ query }).catch(() => {})
    }
  }
}
</script>

<style scoped>
/*
  Clear the fixed Vuetify v-app-bar. The app shell renders page content in a
  plain <main> (not <v-main>), so the app bar does not automatically offset the
  content beneath it. Public pages account for this with a 64px top padding on
  their header section; mirror that here (plus horizontal padding) so the
  "Manage projects" heading and "Add project" button are not hidden behind the
  nav bar.
*/
.project-manager {
  padding: 64px 24px 48px;
}

.project-manager__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}

.project-manager__heading {
  font-size: 1.5rem;
  font-weight: 700;
  color: #41281b;
}
</style>
